import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { SessionV2 } from '../../../types/dashboardV2';
import {
  getMobileSessionStatusBadgeClass,
  getMobileSessionStatusDotClass,
  formatSessionStatus,
} from '../../../shared/utils/statusUtils';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';
import { formatDate, formatTime } from '../../../shared/utils/dateFormat';
import { MobileStat } from '../components/MobileCardParts';

interface MobileSessionCardProps {
  session: SessionV2;
  onViewDetails: (session: SessionV2) => void;
}

// rerender-memo: Memoize list item component to prevent unnecessary re-renders
export const MobileSessionCard = memo(function MobileSessionCard({
  session,
  onViewDetails,
}: MobileSessionCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className="mobile-station-card"
      role="article"
      aria-label={t('dashboard.mobileCards.sessionAria', { id: session.sessionId })}
    >
      {/* Header with Session ID and Status */}
      <div className="mobile-card-header">
        <div>
          <span className="mobile-card-id-label">{t('dashboard.mobileCards.session')}</span>
          <h3 className="mobile-card-id-value">#{session.sessionId}</h3>
        </div>
        <span className={`mobile-status-badge ${getMobileSessionStatusBadgeClass(session.chargingStatus)}`}>
          <span className={`mobile-status-dot ${getMobileSessionStatusDotClass(session.chargingStatus)}`} />
          {formatSessionStatus(session.chargingStatus)}
        </span>
      </div>

      {/* Stats Grid - 2x2 */}
      <div className="mobile-card-stats mobile-card-stats-2x2">
        <MobileStat
          icon="payments"
          label={t('dashboard.mobileCards.amount')}
          value={`${session.amount.toFixed(2)} ₴`}
        />
        <MobileStat
          icon="receipt_long"
          label={t('dashboard.tables.sessions.settled')}
          value={
            session.settledAmount != null ? (
              `${session.settledAmount.toFixed(2)} ₴`
            ) : (
              <span title={t('dashboard.tables.sessions.settledPending')}>—</span>
            )
          }
        />
        <MobileStat
          icon="sell"
          label={t('dashboard.tables.sessions.pricePerKwh')}
          value={
            session.premiumPricePerKwh != null ? (
              <>
                <span style={{ textDecoration: 'line-through', color: 'var(--color-text-secondary, #888)', fontSize: '0.85em', marginRight: 4 }}>
                  {session.stationPrice != null ? `${session.stationPrice.toFixed(2)} ₴` : '—'}
                </span>
                <span style={{ color: 'var(--color-accent, #f59e0b)', fontWeight: 600 }}>
                  {session.premiumPricePerKwh.toFixed(2)} ₴
                </span>
              </>
            ) : session.stationPrice != null ? (
              `${session.stationPrice.toFixed(2)} ₴`
            ) : (
              '—'
            )
          }
        />
        <MobileStat
          icon="bolt"
          label={t('dashboard.mobileCards.charged')}
          value={`${session.energy.toFixed(2)} ${t('dashboard.mobileCards.kwh')}`}
        />
        <MobileStat
          icon="speed"
          label={t('dashboard.tables.sessions.requested')}
          value={`${session.totalQuantity.toFixed(2)} ${t('dashboard.mobileCards.kwh')}`}
        />
        <MobileStat
          icon="schedule"
          label={t('dashboard.mobileCards.date')}
          value={formatDate(session.createdAt)}
        />
      </div>

      {/* User row — the API only exposes the raw user id, so shorten it
          for display and keep the full value on the copy action */}
      {session.userId && (
        <div className="mobile-card-user-row">
          <span className="material-symbols-outlined" aria-hidden="true">person</span>
          <span className="mobile-card-user-label">{t('dashboard.tables.sessions.userId')}</span>
          <span className="mobile-card-user-id" title={session.userId}>
            {session.userId.length > 12
              ? `${session.userId.slice(0, 6)}…${session.userId.slice(-4)}`
              : session.userId}
          </span>
          <CopyIdButton value={session.userId} label={t('dashboard.tables.sessions.copyUserId')} variant="icon" />
        </div>
      )}

      {/* Footer with Station ID, Time and View Button */}
      <div className="mobile-card-footer">
        <div className="mobile-card-address">
          <span className="material-symbols-outlined">ev_station</span>
          <span className="mobile-card-address-text">{session.idTag}</span>
        </div>
        <div className="mobile-card-actions">
          <span className="mobile-session-date">
            {formatTime(session.createdAt)}
          </span>
          <button
            type="button"
            className="mobile-card-action-btn"
            onClick={() => onViewDetails(session)}
            aria-label={t('dashboard.mobileCards.sessionAria', { id: session.sessionId })}
          >
            <span className="material-symbols-outlined">visibility</span>
          </button>
        </div>
      </div>
    </div>
  );
});

export default MobileSessionCard;
